<script setup lang="ts">
  import {
    type SuperUserPhoneNumberRequest as PhoneNumberRequest,
    PhoneNumbersService,
    OrganisationsService,
    type SuperUserRetellAgent as RetellAgent,
    type MinimalOrganisation,
    RetellAgentsService
  } from '@/types/generated'
  import { ref, onMounted, computed } from 'vue'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import * as yup from 'yup'
  import router from '@/router'
  import { ROUTES } from '@/router/routes'
  import { Form, type GenericObject } from 'vee-validate'
  import Breadcrumbs from '@/components/admin/breadcrumbs/Breadcrumbs.vue'
  import { createSuccessToast } from '@/lib/toasts'
  import { useTranslation } from '@/composables/useTranslation'
  import { type Option } from '@/components/admin/form/SelectField.vue'
  import { data } from 'autoprefixer'

  const { t } = useTranslation({
    en: {
      retellAgent: 'Retell agent',
      customer: 'Customer',
      create: 'Create',
      attention: 'Attention',
      attentionText:
        'Creating a phone number will register it with an external system which will incur costs.'
    },
    de: {
      active: 'Aktiv',
      customer: 'Kunde',
      create: 'Erstellen',
      attention: 'Achtung',
      attentionText:
        'Das Erstellen einer Telefonnummer registriert sie bei einem externen System, welches Kosten verursacht.'
    }
  })

  const retellAgentsList = ref<RetellAgent[] | null>(null)
  const retellAgentOptions = computed<Option[]>(() => {
    return (
      retellAgentsList.value?.map((retellAgent) => {
        return {
          value: retellAgent.uuid,
          title:
            retellAgent.agent_name +
            ' - ' +
            retellAgent.voice_id +
            ' (' +
            retellAgent.retell_agent_id +
            ')'
        }
      }) ?? []
    )
  })

  const organisationsList = ref<MinimalOrganisation[]>([])
  const organisationOptions = computed<Option[]>(() => {
    return (
      organisationsList.value?.map((organisation) => {
        return {
          value: organisation.uuid,
          title: organisation.name ?? ''
        }
      }) ?? []
    )
  })

  const isProcessing = ref(false)

  const handleSubmit = async (values: GenericObject) => {
    isProcessing.value = true
    PhoneNumbersService.phoneNumbersCreate({ requestBody: values as PhoneNumberRequest })
      .then((phoneNumber) => {
        createSuccessToast()
        router.push(ROUTES.ADMIN.VOICE.PHONE_NUMBER_DETAIL.replace(':uuid', phoneNumber.uuid))
      })
      .finally(() => {
        isProcessing.value = false
      })
  }

  onMounted(async () => {
    retellAgentsList.value = (await RetellAgentsService.retellAgentsList()).results as RetellAgent[]
    // Response is a plain array - cast since OpenAPI incorrectly generates paginated type
    const orgsResp = await OrganisationsService.organisationsOrganisationsListList()
    organisationsList.value = orgsResp as unknown as MinimalOrganisation[]
  })
</script>

<template>
  <div class="space-y-5 p-2 sm:p-5 sm:py-0 md:pt-5">
    <!-- Page Header -->
    <div class="flex items-center justify-between gap-x-5">
      <Breadcrumbs
        :items="[
          { title: 'Dashboard', to: ROUTES.ADMIN.DASHBOARD },
          { title: 'Phone numbers', to: ROUTES.ADMIN.VOICE.PHONE_NUMBER_LIST },
          { title: 'Create phone number', to: ROUTES.ADMIN.VOICE.PHONE_NUMBER_CREATE }
        ]"
      />
      <div class="flex items-center justify-end gap-x-2"></div>
    </div>

    <div
      class="flex flex-col rounded-xl border border-gray-200 bg-white p-5 shadow-sm dark:border-neutral-700
        dark:bg-neutral-800"
    >
      <div class="w-full md:w-1/2">
        <Form
          v-slot="{ errors }"
          @submit="handleSubmit"
        >
          <div
            class="border-s-4 border-red-500 bg-red-50 p-4 dark:bg-red-800/30"
            role="alert"
            tabindex="-1"
            aria-labelledby="hs-bordered-red-style-label"
          >
            <div class="flex">
              <div class="ms-3">
                <h3
                  id="hs-bordered-red-style-label"
                  class="font-semibold text-gray-800 dark:text-white"
                >
                  {{ t('attention') }}
                </h3>
                <p class="text-sm text-gray-700 dark:text-neutral-400">
                  {{ t('attentionText') }}
                </p>
              </div>
            </div>
          </div>
          <SelectField
            :label="t('customer')"
            :is-filterable="true"
            name="org_uuid"
            :rules="yup.string().uuid().required()"
            :options="organisationOptions"
            :disabled="isProcessing"
          />
          <SelectField
            :label="t('retellAgent')"
            :is-filterable="false"
            name="agent_uuid"
            :rules="yup.string().uuid().required()"
            :options="retellAgentOptions"
            :disabled="isProcessing"
          />
          <button
            class="mt-3 inline-flex items-center gap-x-2 rounded-lg border border-transparent bg-purple-600 px-3 py-2
              text-sm font-semibold text-white hover:bg-purple-700 focus:outline-none focus:ring-2
              focus:ring-blue-500 disabled:pointer-events-none disabled:opacity-50"
            type="submit"
            :disabled="Object.keys(errors).length > 0 || isProcessing"
          >
            {{ t('create') }}
          </button>
        </Form>
      </div>
    </div>
  </div>
</template>
